import type { QueryObserverOptions, useQueryClient } from "@tanstack/react-query"
import type { OpenapiQueryClient } from "openapi-react-query"

import { $rqCreateApiClient } from "@/api/create"
import { $rqViewsApiClient } from "@/api/groups"
import { $rqIotApiClient } from "@/api/iot"
import { $rqOtaApiClient } from "@/api/ota"
import { $rqProvisioningApiClient } from "@/api/provisioning"
import { query } from "@/api/query"
import { getXOrganizationHeaders } from "@/api/utils"
import { findQueriesByEndpoint } from "@/api/utils/findQueriesByEndpoint"
import { sketchbookRoot } from "@/constants/sketches"
import type { AssetType } from "@/types"
import type { SafeAny } from "@/types/utils"

/**
 * A hook for invalidating all queries in the query cache.
 *
 * TODO: Add support for space-scoped invalidation
 */

export function invalidateCachedQueries(spaceId: string, queryClient: ReturnType<typeof useQueryClient>) {
  /** Invalidates all queries in the query cache. */
  const invalidateAll = async (options?: { invalidateInfinite?: boolean }) => {
    await Promise.allSettled(
      queryClient
        .getQueryCache()
        .getAll()
        .filter(q => {
          // If we want to invalidate infinite queries, we need to check if any observers are infinite.

          const isInfinite =
            q.observers.some(observer => observer.options.staleTime === Infinity) ||
            // ! this field could actually exist.
            (q.options as QueryObserverOptions).staleTime === Infinity

          return isInfinite ? options?.invalidateInfinite : true
        })
        .map(q => queryClient.invalidateQueries({ queryKey: q.queryKey }))
    )
  }

  /** Invalidates all queries related to a specific group kind. */
  const invalidateByGroupKind = async (
    kind: Exclude<AssetType, "FOLDER">,
    opts?: {
      id?: string
      folderId?: string
    }
  ) => {
    const cachedQueries = queryClient.getQueryCache().getAll()

    // TODO: Check for parallelization improvements
    switch (kind) {
      case "DEVICE":
        if (opts?.id) {
          await queryClient.invalidateQueries(
            $rqIotApiClient.queryOptions("get", "/iot/v2/devices/{id}", {
              params: { header: getXOrganizationHeaders(spaceId), path: { id: opts.id } }
            })
          )
          await queryClient.invalidateQueries({
            queryKey: $rqIotApiClient
              .queryOptions("get", "/iot/v2/devices/{id}/status", {
                params: { header: getXOrganizationHeaders(spaceId), path: { id: opts.id } }
              })
              .queryKey.slice(0, 2) // Remove the params property, so we invalidate every call to this endpoint.
          })

          await queryClient.invalidateQueries({
            queryKey: $rqOtaApiClient
              .queryOptions("get", "/v1/ota", {
                params: {
                  header: getXOrganizationHeaders(spaceId),
                  query: { device_id: opts.id }
                }
              })
              .queryKey.slice(0, 2) // Remove the params property, so we invalidate every call to this endpoint.
          })
        }

        await queryClient.invalidateQueries(
          $rqViewsApiClient.queryOptions("get", "/groups/v1/devices", {
            params: { header: getXOrganizationHeaders(spaceId) }
          })
        )

        await queryClient.invalidateQueries(
          $rqProvisioningApiClient.queryOptions("get", "/v1/onboarding", {
            params: { header: getXOrganizationHeaders(spaceId) }
          })
        )

        break
      case "THING":
        if (opts?.id) {
          await Promise.allSettled(
            findQueriesByEndpoint<typeof $rqIotApiClient>(cachedQueries, [
              {
                endpoint: "/iot/v2/things/{id}",
                options: {
                  paramsFilter: [getXOrganizationHeaders(spaceId)?.["X-Organization"], opts.id],
                  paramsFilterMatch: "exact"
                }
              },
              { endpoint: "/iot/v1/sync/{id}" }
            ]).map(q => queryClient.invalidateQueries({ queryKey: q.queryKey }))
          )
        }

        await queryClient.invalidateQueries(
          $rqViewsApiClient.queryOptions("get", "/groups/v1/things", {
            params: { header: getXOrganizationHeaders(spaceId) }
          })
        )

        break
      case "DASHBOARD":
        if (opts?.id) {
          // Endpoint not in use.
        }

        await queryClient.invalidateQueries(
          $rqViewsApiClient.queryOptions("get", "/groups/v1/dashboards", {
            params: { header: getXOrganizationHeaders(spaceId) }
          })
        )

        await queryClient.invalidateQueries({
          queryKey: $rqIotApiClient
            .queryOptions("get", "/iot/v3/dashboards", {
              params: { header: getXOrganizationHeaders(spaceId) }
            })
            .queryKey.slice(0, 2) // Remove the params property, so we invalidate every call to this endpoint.
        })

        break

      case "SKETCH":
        if (opts?.id) {
          // Endpoint not in use.
        }

        await queryClient.invalidateQueries(
          $rqViewsApiClient.queryOptions("get", "/groups/v1/sketches", {
            params: { header: getXOrganizationHeaders(spaceId) }
          })
        )

        await queryClient.invalidateQueries(
          $rqCreateApiClient.queryOptions("get", "/create/v2/files/d/{path}", {
            params: {
              header: getXOrganizationHeaders(spaceId),
              path: { path: sketchbookRoot },
              query: { deep: true }
            }
          })
        )

        break

      case "TRIGGER":
        if (opts?.id) {
          await queryClient.invalidateQueries(
            $rqIotApiClient.queryOptions("get", "/iot/v1/triggers/{id}", {
              params: { header: getXOrganizationHeaders(spaceId), path: { id: opts.id } }
            })
          )
        }

        await queryClient.invalidateQueries(
          $rqViewsApiClient.queryOptions("get", "/groups/v1/triggers", {
            params: { header: getXOrganizationHeaders(spaceId) }
          })
        )

        break
      default:
        break
    }

    // Either invalidate the specific folder, or all groups.
    if (opts?.folderId) {
      await queryClient.invalidateQueries({
        queryKey: $rqViewsApiClient
          .queryOptions("get", "/groups/v1/all", {
            params: {
              header: getXOrganizationHeaders(spaceId),
              query: { parent_id: opts.folderId }
            }
          })
          .queryKey.slice(0, 2) // Remove the params property, so we invalidate every call to this endpoint.
      })
    } else {
      await queryClient.invalidateQueries({
        queryKey: $rqViewsApiClient
          .queryOptions("get", "/groups/v1/all", {
            params: { header: getXOrganizationHeaders(spaceId) }
          })
          .queryKey.slice(0, 2) // Remove the params property, so we invalidate every call to this endpoint.
      })
    }

    // Invalidate the subscriptions-api endpoint.
    await queryClient.invalidateQueries(query.user.subscriptions)
    await queryClient.invalidateQueries(query.space.subscriptions(spaceId))
  }

  /** Invalidates all queries matching the given endpoint(s), with queryClient pre-bound. */
  const invalidateByEndpoint = async <T extends OpenapiQueryClient<SafeAny, SafeAny>>(
    targetEndpoint: Parameters<typeof findQueriesByEndpoint<T>>[1]
  ) => {
    const cachedQueries = queryClient.getQueryCache().getAll()
    await Promise.allSettled(
      findQueriesByEndpoint<T>(cachedQueries, targetEndpoint).map(q =>
        queryClient.invalidateQueries({ queryKey: q.queryKey })
      )
    )
  }

  /** Invalidates a custom query key. */
  const invalidateCustom = queryClient.invalidateQueries.bind(queryClient)

  return {
    invalidateAll,
    invalidateByGroupKind,
    invalidateByEndpoint,
    invalidateCustom
  }
}
